import { useMemo, useState } from "react";
import type { FunctionComponent, MouseEvent, ReactNode } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import AirplanemodeActiveOutlinedIcon from "@mui/icons-material/AirplanemodeActiveOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MenuIcon from "@mui/icons-material/Menu";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";

import { appRouteCatalog, getRoutePath, getRouteDefinition } from "../routes/catalog";
import type { NavigationPolicy } from "../routes/catalog";
import type { ThemeMode } from "../theme";
import logoDark from "../assets/smartefb-logo-dark.png";
import logoLight from "../assets/smartefb-logo-light.png";

const BRAND_LABEL = "smartEFB by COCKPIT-IT";

export type ShellNavItem = {
  id: NavigationPolicy;
  label: string;
  to: string;
};

type Props = {
  isAuthenticated: boolean;
  isAcmiClient: boolean;
  isRefuelEuReader?: boolean;
  navItems: ShellNavItem[];
  email?: string;
  operatorName?: string;
  roleLabel?: string;
  canUseViewAs: boolean;
  isViewAsActive: boolean;
  viewAsButtonLabel: string;
  themeMode: ThemeMode;
  isMessagingEnabled: boolean;
  unreadMessageCount: number;
  navigationBadges?: Partial<Record<NavigationPolicy, number | null>>;
  onNavIntent: (itemId: NavigationPolicy) => void;
  onOpenViewAs: () => void;
  onOpenMessages: () => void;
  onToggleTheme: () => void;
  onSignout: () => void;
};

const DESKTOP_WIDTH = 212;
const RAIL_WIDTH = 72;
const MOBILE_HEADER_HEIGHT = 56;

export const normalizeNavigationBadgeCount = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

const navIcons: Record<NavigationPolicy, ReactNode> = {
  ops: <DashboardOutlinedIcon />,
  "journey-logs": <MenuBookOutlinedIcon />,
  transfer: <SwapHorizOutlinedIcon />,
  flights: <FlightTakeoffOutlinedIcon />,
  "improve-operation": <InsightsOutlinedIcon />,
  files: <DescriptionOutlinedIcon />,
  library: <FolderOutlinedIcon />,
  fuel: <LocalGasStationOutlinedIcon />,
  reports: <AssessmentOutlinedIcon />,
  dashboard: <SpaceDashboardOutlinedIcon />,
  fleets: <AirplanemodeActiveOutlinedIcon />,
  admin: <AdminPanelSettingsOutlinedIcon />,
};

export const getActiveNavigationItemId = (
  pathname: string,
  items: ShellNavItem[],
): NavigationPolicy | null => {
  const matches = items.filter(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
  const directMatch = matches.sort((left, right) => right.to.length - left.to.length)[0];
  if (directMatch) {
    return directMatch.id;
  }

  let matchedRoute = appRouteCatalog.find(
    (route) => route.path !== "*" && Boolean(matchPath({ path: route.path, end: true }, pathname)),
  );
  while (matchedRoute) {
    if (matchedRoute.navigationPolicy && items.some((item) => item.id === matchedRoute?.navigationPolicy)) {
      return matchedRoute.navigationPolicy;
    }
    matchedRoute = matchedRoute.parentId
      ? getRouteDefinition(matchedRoute.parentId) || undefined
      : undefined;
  }
  return null;
};

const brand = (themeMode: ThemeMode, compact = false, size: "bar" | "nav" = "nav") => (
  <Stack
    component={RouterLink}
    to={getRoutePath("home")}
    direction="row"
    alignItems="center"
    sx={{ color: "var(--text)", textDecoration: "none", minWidth: 0, maxWidth: "100%" }}
    aria-label={BRAND_LABEL}
  >
    <Box
      component="img"
      src={themeMode === "light" ? logoLight : logoDark}
      alt=""
      draggable={false}
      sx={
        compact
          ? {
              width: 36,
              height: 36,
              objectFit: "cover",
              objectPosition: "left center",
              flexShrink: 0,
              display: "block",
            }
          : {
              height: size === "bar" ? 36 : 44,
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }
      }
    />
  </Stack>
);

export const AppNavigationShell: FunctionComponent<Props> = ({
  isAuthenticated,
  isAcmiClient,
  isRefuelEuReader = false,
  navItems,
  email,
  operatorName,
  roleLabel,
  canUseViewAs,
  isViewAsActive,
  viewAsButtonLabel,
  themeMode,
  isMessagingEnabled,
  unreadMessageCount,
  navigationBadges = {},
  onNavIntent,
  onOpenViewAs,
  onOpenMessages,
  onToggleTheme,
  onSignout,
}) => {
  const { pathname } = useLocation();
  const activeItemId = useMemo(
    () => getActiveNavigationItemId(pathname, navItems),
    [navItems, pathname],
  );
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<HTMLElement | null>(null);
  const userInitial = email?.trim().charAt(0).toUpperCase() || null;

  const closeMobileDrawer = () => setIsMobileDrawerOpen(false);
  const openUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const closeUserMenu = () => setAnchorElUser(null);

  const renderNavItems = (compact: boolean, closeAfterSelect?: () => void) => (
    <List component="nav" aria-label="Primary navigation" disablePadding>
      {navItems.map((item) => {
        const badgeCount = normalizeNavigationBadgeCount(navigationBadges[item.id]);
        const tooltipLabel = badgeCount > 0 ? `${item.label}, ${badgeCount} pending` : item.label;
        const button = (
          <ListItemButton
            key={item.id}
            component={RouterLink}
            to={item.to}
            selected={activeItemId === item.id}
            onClick={closeAfterSelect}
            onMouseEnter={() => onNavIntent(item.id)}
            onFocus={() => onNavIntent(item.id)}
            aria-current={activeItemId === item.id ? "page" : undefined}
            aria-label={tooltipLabel}
            sx={{
              minHeight: 40,
              justifyContent: compact ? "center" : "flex-start",
              px: compact ? 1 : 1.25,
              mb: 0.25,
              borderRadius: "9px",
              color: "var(--muted)",
              "&.Mui-selected": {
                color: "var(--blue)",
                backgroundColor: "var(--blue-bg)",
              },
              "&.Mui-selected:hover": { backgroundColor: "var(--blue-bg)" },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: compact ? 0 : 36,
                color: "inherit",
                justifyContent: "center",
                "& svg": { fontSize: 20 },
              }}
            >
              <Badge
                color="error"
                badgeContent={badgeCount}
                max={99}
                invisible={badgeCount <= 0}
              >
                {navIcons[item.id]}
              </Badge>
            </ListItemIcon>
            {!compact ? (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: activeItemId === item.id ? 600 : 500 }}
              />
            ) : null}
          </ListItemButton>
        );

        return compact ? (
          <Tooltip key={item.id} title={tooltipLabel} placement="right">
            {button}
          </Tooltip>
        ) : button;
      })}

      {!isAcmiClient && !isRefuelEuReader ? (
        <Tooltip title={compact ? "Support" : ""} placement="right">
          <ListItemButton
            className="show-zammad-form"
            onClick={closeAfterSelect}
            sx={{
              minHeight: 40,
              justifyContent: compact ? "center" : "flex-start",
              px: compact ? 1 : 1.25,
              borderRadius: "9px",
              color: "var(--muted)",
            }}
          >
            <ListItemIcon sx={{ minWidth: compact ? 0 : 36, color: "inherit", justifyContent: "center" }}>
              <HelpOutlineOutlinedIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            {!compact ? <ListItemText primary="Support" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} /> : null}
          </ListItemButton>
        </Tooltip>
      ) : null}
    </List>
  );

  const messageAction = (compact: boolean) => isMessagingEnabled ? (
    <Tooltip title="Flight messages" placement={compact ? "right" : "bottom"}>
      <IconButton
        onClick={onOpenMessages}
        aria-label="Open flight messages"
        sx={{ color: "var(--muted)" }}
      >
        <Badge color="error" badgeContent={unreadMessageCount} invisible={unreadMessageCount <= 0}>
          <MailOutlineIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  ) : null;

  const desktopFooter = (compact: boolean) => (
    <Stack spacing={1} sx={{ flexShrink: 0, pt: 1, borderTop: "1px solid var(--line)" }}>
      <Stack direction={compact ? "column" : "row"} spacing={0.5} justifyContent="center">
        {messageAction(compact)}
        <Tooltip title={themeMode === "dark" ? "Use light theme" : "Use dark theme"} placement={compact ? "right" : "bottom"}>
          <IconButton onClick={onToggleTheme} aria-label={`Use ${themeMode === "dark" ? "light" : "dark"} theme`} sx={{ color: "var(--muted)" }}>
            {themeMode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {canUseViewAs ? (
        <Tooltip title={compact ? viewAsButtonLabel : ""} placement="right">
          <Button
            onClick={onOpenViewAs}
            aria-label={viewAsButtonLabel}
            sx={{
              minWidth: 0,
              justifyContent: compact ? "center" : "flex-start",
              px: compact ? 1 : 1.25,
              py: 1,
              border: "1px solid var(--line)",
              backgroundColor: isViewAsActive ? "var(--blue-bg)" : "var(--panel2)",
              color: isViewAsActive ? "var(--blue)" : "var(--muted)",
            }}
          >
            <AccountCircleOutlinedIcon sx={{ fontSize: 19, flexShrink: 0 }} />
            {!compact ? (
              <Typography noWrap sx={{ ml: 1, fontSize: 12, fontWeight: 600, textTransform: "none" }}>
                {viewAsButtonLabel}
              </Typography>
            ) : null}
          </Button>
        </Tooltip>
      ) : null}

      <Tooltip title={compact ? "Open user menu" : ""} placement="right">
        <Button
          onClick={openUserMenu}
          aria-label="Open user menu"
          sx={{
            minWidth: 0,
            justifyContent: compact ? "center" : "flex-start",
            px: compact ? 0.5 : 0.75,
            py: 0.5,
            color: "var(--text)",
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: "var(--panel3)", color: "var(--blue)", border: "1px solid var(--line2)", fontSize: 12, fontWeight: 700 }}>
            {userInitial || <PersonOutlineOutlinedIcon fontSize="small" />}
          </Avatar>
          {!compact ? (
            <Box sx={{ ml: 1, minWidth: 0, textAlign: "left", textTransform: "none" }}>
              <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, maxWidth: 135 }}>
                {operatorName || email || "User"}
              </Typography>
              <Typography noWrap sx={{ color: "var(--faint)", fontSize: 10.5, maxWidth: 135 }}>
                {roleLabel || email || "Account"}
              </Typography>
            </Box>
          ) : null}
        </Button>
      </Tooltip>
    </Stack>
  );

  if (!isAuthenticated) {
    return (
      <AppBar
        className="app-shell-navigation"
        position="fixed"
        elevation={0}
        sx={{
          height: 64,
          justifyContent: "center",
          backgroundColor: "var(--panel)",
          color: "var(--text)",
          borderBottom: "1px solid var(--line)",
          backgroundImage: "none",
          "@media print": { display: "none" },
        }}
      >
        <Toolbar sx={{ minHeight: "64px !important", px: { xs: 2, sm: 3 } }}>
          {brand(themeMode, false, "bar")}
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <Box
        className="app-shell-navigation"
        component="aside"
        data-testid="app-sidebar"
        sx={{
          position: "fixed",
          inset: "0 auto 0 0",
          zIndex: (theme) => theme.zIndex.appBar,
          display: "none",
          flexDirection: "column",
          width: RAIL_WIDTH,
          p: "18px 10px 14px",
          backgroundColor: "var(--panel)",
          borderRight: "1px solid var(--line)",
          "@media (min-width: 768px)": { display: "flex" },
          "@media (min-width: 1280px)": { display: "flex", width: DESKTOP_WIDTH, p: "18px 12px 16px" },
          "@media print": { display: "none" },
        }}
      >
        <Box sx={{ px: 0.75, pb: 2, width: "100%", minWidth: 0, display: "flex", justifyContent: "center", "@media (min-width: 1280px)": { justifyContent: "flex-start" } }}>
          <Box sx={{ display: "block", "@media (min-width: 1280px)": { display: "none" } }}>{brand(themeMode, true)}</Box>
          <Box sx={{ display: "none", minWidth: 0, maxWidth: "100%", "@media (min-width: 1280px)": { display: "block" } }}>{brand(themeMode, false, "nav")}</Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", pr: 0.25 }}>
          <Box sx={{ display: "block", "@media (min-width: 1280px)": { display: "none" } }}>{renderNavItems(true)}</Box>
          <Box sx={{ display: "none", "@media (min-width: 1280px)": { display: "block" } }}>{renderNavItems(false)}</Box>
        </Box>
        <Box sx={{ display: "block", "@media (min-width: 1280px)": { display: "none" } }}>{desktopFooter(true)}</Box>
        <Box sx={{ display: "none", "@media (min-width: 1280px)": { display: "block" } }}>{desktopFooter(false)}</Box>
      </Box>

      <AppBar
        className="app-shell-navigation"
        data-testid="app-mobile-header"
        position="fixed"
        elevation={0}
        sx={{
          display: "flex",
          height: MOBILE_HEADER_HEIGHT,
          justifyContent: "center",
          backgroundColor: "var(--panel)",
          color: "var(--text)",
          borderBottom: "1px solid var(--line)",
          backgroundImage: "none",
          "@media (min-width: 768px)": { display: "none" },
          "@media print": { display: "none" },
        }}
      >
        <Toolbar sx={{ minHeight: `${MOBILE_HEADER_HEIGHT}px !important`, px: 1.5 }}>
          {brand(themeMode, false, "bar")}
          <Box sx={{ flex: 1 }} />
          {messageAction(false)}
          <IconButton onClick={openUserMenu} aria-label="Open user menu" sx={{ color: "var(--text)", ml: 0.5 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: "var(--panel3)", color: "var(--blue)", fontSize: 12 }}>
              {userInitial || <PersonOutlineOutlinedIcon fontSize="small" />}
            </Avatar>
          </IconButton>
          <IconButton onClick={() => setIsMobileDrawerOpen(true)} aria-label="Open navigation menu" sx={{ color: "var(--text)", ml: 0.5 }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        className="app-shell-navigation"
        anchor="left"
        open={isMobileDrawerOpen}
        onClose={closeMobileDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 288,
            maxWidth: "calc(100vw - 32px)",
            p: 2,
            backgroundColor: "var(--panel)",
            color: "var(--text)",
            backgroundImage: "none",
          },
        }}
      >
        <Box sx={{ pb: 2, minWidth: 0 }}>{brand(themeMode, false, "nav")}</Box>
        <Divider sx={{ borderColor: "var(--line)", mb: 1.5 }} />
        <Box sx={{ flex: 1, overflowY: "auto" }}>{renderNavItems(false, closeMobileDrawer)}</Box>
        <Divider sx={{ borderColor: "var(--line)", my: 1.5 }} />
        <Stack spacing={0.5}>
          {canUseViewAs ? (
            <ListItemButton onClick={() => { closeMobileDrawer(); onOpenViewAs(); }} sx={{ borderRadius: "9px" }}>
              <ListItemIcon><AccountCircleOutlinedIcon /></ListItemIcon>
              <ListItemText primary={viewAsButtonLabel} />
            </ListItemButton>
          ) : null}
          {isMessagingEnabled ? (
            <ListItemButton onClick={() => { closeMobileDrawer(); onOpenMessages(); }} sx={{ borderRadius: "9px" }}>
              <ListItemIcon>
                <Badge color="error" badgeContent={unreadMessageCount} invisible={unreadMessageCount <= 0}><MailOutlineIcon /></Badge>
              </ListItemIcon>
              <ListItemText primary="Messages" />
            </ListItemButton>
          ) : null}
          {!isAcmiClient ? (
            <ListItemButton component={RouterLink} to={getRoutePath("profile")} onClick={closeMobileDrawer} sx={{ borderRadius: "9px" }}>
              <ListItemIcon><PersonOutlineOutlinedIcon /></ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItemButton>
          ) : null}
          <ListItemButton onClick={onToggleTheme} sx={{ borderRadius: "9px" }}>
            <ListItemIcon>{themeMode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}</ListItemIcon>
            <ListItemText primary={`Use ${themeMode === "dark" ? "light" : "dark"} theme`} />
          </ListItemButton>
          <ListItemButton onClick={() => { closeMobileDrawer(); onSignout(); }} sx={{ borderRadius: "9px" }}>
            <ListItemIcon><LogoutOutlinedIcon /></ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </Stack>
      </Drawer>

      <Menu
        id="menu-user"
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={closeUserMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canUseViewAs ? (
          <MenuItem onClick={() => { closeUserMenu(); onOpenViewAs(); }}>
            <AccountCircleOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            {viewAsButtonLabel}
          </MenuItem>
        ) : null}
        {!isAcmiClient ? (
          <MenuItem component={RouterLink} to={getRoutePath("profile")} onClick={closeUserMenu}>
            <PersonOutlineOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            Profile
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => { closeUserMenu(); onToggleTheme(); }}>
          {themeMode === "dark" ? <LightModeOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> : <DarkModeOutlinedIcon fontSize="small" sx={{ mr: 1 }} />}
          Use {themeMode === "dark" ? "light" : "dark"} theme
        </MenuItem>
        <MenuItem onClick={() => { closeUserMenu(); onSignout(); }}>
          <LogoutOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>
    </>
  );
};
